import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Icon } from "../icon";

type ConfirmDialogProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  content: React.ReactNode;
  okText?: string;
  okButtonProps?: React.ComponentProps<typeof Button>;
  cancelText?: string;
  cancelButtonProps?: React.ComponentProps<typeof Button>;
  onOk?: () => Promise<boolean> | Promise<void>;
  onCancel?: () => Promise<void>;
} & React.ComponentProps<typeof Dialog>;

const ConfirmDialog = ({
  title,
  description,
  content,
  open,
  onOpenChange,
  okText,
  cancelText,
  onOk,
  onCancel,
  okButtonProps,
  cancelButtonProps,
  ...rest
}: ConfirmDialogProps) => {
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    setSubmitting(true);
    try {
      if (onOk) {
        const result = await onOk();
        if (result === false) {
          return; // 如果 onOk 返回 false，则不关闭对话框
        }
      }
      onOpenChange?.(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (onCancel) {
      await onCancel();
    }
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...rest}>
      <DialogContent aria-describedby={description ? "confirm-dialog-description" : undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {description && <DialogDescription>{description}</DialogDescription>}

        <div className="text-base">{content}</div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleCancel} {...cancelButtonProps}>
            {cancelText || t("common.cancelText")}
          </Button>
          <Button disabled={submitting} onClick={handleOk} {...okButtonProps}>
            {submitting && <Icon icon="lucide:loader" className="animate-spin mr-2" />}
            {okText || t("common.confirmText")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmDialog, type ConfirmDialogProps };
